import Default from "layouts/auth/types/Default";
import React, { useCallback, useEffect, useState } from "react";
import {
  verifyPhoneCode,
  resendPhoneCode,
  addPhoneNumber,
  getUserProfile,
} from "api";
import { useNavigate } from "react-router-dom";
import { debounce } from "lodash";
import { useAuth } from "contexts/authContext";
import { 
  FaCheckCircle, 
  FaExclamationTriangle,
  FaArrowRight
} from "react-icons/fa";
import PhoneInput from "components/fields/PhoneInput";
import OTPInput from "components/fields/OTPInput";
import VerificationBanner from "components/verification/VerificationBanner";
import SuccessBanner from "components/verification/SuccessBanner";

const LoadingSpinner = () => (
  <div className="flex h-screen items-center justify-center bg-white dark:bg-navy-900">
    <div className="relative">
      <div className="h-16 w-16 animate-spin rounded-full border-4 border-dashed border-orange-500" />
      <div className="absolute inset-0 h-16 w-16 animate-ping rounded-full border-4 border-orange-300 opacity-20" />
    </div>
  </div>
);

function PhoneVerificationDefault() {
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [phone, setPhone] = useState("");
  const [signupPhone, setSignupPhone] = useState("");
  const [selectedPhoneOption, setSelectedPhoneOption] = useState("signup");
  const [codeSent, setCodeSent] = useState(false);
  const [verificationComplete, setVerificationComplete] = useState(false);
  const [resendAttempts, setResendAttempts] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [codeExpiry, setCodeExpiry] = useState(null);
  const [phoneValidation, setPhoneValidation] = useState({ isValid: false, message: "" });

  const navigate = useNavigate();
  const { refreshUser } = useAuth();

  // Fetch user profile on mount
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const profile = await getUserProfile();
        if (profile?.user?.phoneNumber) {
          setSignupPhone(profile.user.phoneNumber);
          setPhone(profile.user.phoneNumber);
          setPhoneValidation({ isValid: true, message: "✓ Using registered number" });
        }
      } catch (err) {
        console.error("Failed to fetch profile:", err);
      }
    };
    fetchProfile();
  }, []);

  const handleSendCode = async () => {
    if (!phone || !phoneValidation.isValid) {
      setError("Please enter a valid phone number.");
      return;
    }
    setIsLoading(true);
    try {
      let response;
      if (selectedPhoneOption === "signup" && phone === signupPhone) {
        response = await resendPhoneCode();
      } else {
        response = await addPhoneNumber(phone);
        if (response.message?.includes("added")) setSignupPhone(phone);
      }

      if (
        response.message === "Verification code resent" ||
        response.message === "Verification code re-sent to existing number" ||
        response.message === "Phone number added; verification code sent"
      ) {
        setSuccess("✅ Verification code sent successfully!");
        setCodeSent(true);
        setResendAttempts((n) => n + 1);
        setCode(["", "", "", "", "", ""]);
        setCodeExpiry(Date.now() + 5 * 60 * 1000); // 5 minutes expiry
        await refreshUser();
      } else if (response.message === "Phone already verified") {
        setSuccess("🎉 Your phone number is already verified!");
        await refreshUser();
        setTimeout(() => navigate("/admin/dashboard", { state: { from: "phone-verification" } }), 1500);
      } else {
        setError("Failed to send code. Please try again.");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to send code. Please try again.");
      console.error("Send code error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerificationClick = useCallback(
    async (codeString) => {
      if (!codeString || codeString.length !== 6) return;

      setIsLoading(true);
      try {
        const response = await verifyPhoneCode({ code: codeString });
        if (response.message === "Phone number verified successfully") {
          setVerificationComplete(true);
          setSuccess("🎉 Phone number verified successfully!");
          await refreshUser();
        }
      } catch (err) {
        setError(err.response?.data?.message || "Invalid verification code. Please try again.");
        setCode(["", "", "", "", "", ""]);
      } finally {
        setIsLoading(false);
      }
    },
    [refreshUser]
  );

  const handleResendCode = async () => {
    if (resendAttempts >= 3) return;
    setIsLoading(true);
    try {
      const response = await resendPhoneCode();
      if (response.message === "Verification code resent") {
        setSuccess("✅ New code sent to your phone!");
        setCodeExpiry(Date.now() + 5 * 60 * 1000);
        setResendAttempts((n) => n + 1);
        setCode(["", "", "", "", "", ""]);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to resend code. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePhoneChange = (newPhone) => {
    setPhone(newPhone);
    setError("");
  };

  const handlePhoneValidationChange = (validation) => {
    setPhoneValidation(validation);
  };

  const handleCodeChange = (newCode) => {
    setCode(newCode);
    setError("");
  };

  const handleCodeComplete = (codeString) => {
    handleVerificationClick(codeString);
  };

  const handleContinueToDashboard = () => {
    navigate("/admin/dashboard", { state: { from: "phone-verification" } });
  };

  if (isLoading) return <LoadingSpinner />;

  // Show success banner when verification is complete
  if (verificationComplete) {
    return (
      <Default
        maincard={
          <div className="flex flex-col w-full max-w-[480px] px-4 md:px-0 mt-[8vh] z-10">
            <div className="flex flex-col bg-white dark:bg-navy-900 rounded-3xl p-8 md:p-10 shadow-xl transition-all duration-300 border border-gray-100 dark:border-navy-800">
              <SuccessBanner
                title="Verification Complete! 🎉"
                subtitle="Your phone number has been successfully verified and your account is now secured with two-factor authentication."
                phoneNumber={phone}
                onContinue={handleContinueToDashboard}
                continueText="Continue to Dashboard"
              />
            </div>
          </div>
        }
      />
    );
  }

  return (
    <Default
      maincard={
        <div className="flex flex-col w-full max-w-[480px] px-4 md:px-0 mt-[8vh] z-10">
          <div className="flex flex-col bg-white dark:bg-navy-900 rounded-3xl p-8 md:p-10 shadow-xl transition-all duration-300 border border-gray-100 dark:border-navy-800">
            {/* Enhanced Verification Banner */}
            <VerificationBanner
              step={codeSent ? 2 : 1}
              totalSteps={2}
              title="Two-Step Verification"
              subtitle={codeSent 
                ? "Enter the verification code sent to your phone to complete the setup"
                : "Secure your account by adding a phone number for enhanced security"
              }
              currentStep="Add Phone Number"
              nextStep="Verify Code"
              phoneNumber={codeSent ? phone : ""}
              codeExpiry={codeExpiry}
              showTimer={codeSent}
            />

            {/* Alerts */}
            {error && (
              <div className="mb-6 flex items-start rounded-xl bg-red-50 dark:bg-red-900/20 p-4 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800">
                <FaExclamationTriangle className="mr-3 h-5 w-5 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <span className="text-base font-medium">{error}</span>
                  <p className="text-sm mt-1 text-red-500 dark:text-red-300">
                    Please check your input and try again.
                  </p>
                </div>
              </div>
            )}
            
            {success && (
              <div className="mb-6 flex items-start rounded-xl bg-green-50 dark:bg-green-900/20 p-4 text-green-600 dark:text-green-400 border border-green-200 dark:border-green-800">
                <FaCheckCircle className="mr-3 h-5 w-5 mt-0.5 flex-shrink-0" />
                <span className="text-base font-medium">{success}</span>
              </div>
            )}

            {!codeSent && (
              <div className="space-y-6">
                {/* Phone Options */}
                {signupPhone && (
                  <div className="space-y-3">
                    <label className="flex items-center gap-3 p-4 rounded-xl border-2 border-gray-200 dark:border-navy-700 hover:border-orange-300 dark:hover:border-orange-500 transition-colors cursor-pointer">
                      <input
                        type="radio"
                        name="phoneOption"
                        value="signup"
                        checked={selectedPhoneOption === "signup"}
                        onChange={() => {
                          setSelectedPhoneOption("signup");
                          setPhone(signupPhone);
                          setPhoneValidation({ isValid: true, message: "✓ Using registered number" });
                        }}
                        className="h-5 w-5 text-orange-500 focus:ring-orange-500"
                        aria-label="Use registered phone number"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-base font-medium text-gray-700 dark:text-gray-300">
                            Use registered phone
                          </span>
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            {signupPhone}
                          </span>
                        </div>
                      </div>
                    </label>
                  </div>
                )}
                
                <label className="flex items-center gap-3 p-4 rounded-xl border-2 border-gray-200 dark:border-navy-700 hover:border-orange-300 dark:hover:border-orange-500 transition-colors cursor-pointer">
                  <input
                    type="radio"
                    name="phoneOption"
                    value="new"
                    checked={selectedPhoneOption === "new"}
                    onChange={() => {
                      setSelectedPhoneOption("new");
                      setPhone("");
                      setPhoneValidation({ isValid: false, message: "" });
                    }}
                    className="h-5 w-5 text-orange-500 focus:ring-orange-500"
                    aria-label="Use a new phone number"
                  />
                  <div className="flex-1">
                    <span className="text-base font-medium text-gray-700 dark:text-gray-300">
                      Use a new phone number
                    </span>
                  </div>
                </label>

                {/* Phone Input */}
                {selectedPhoneOption === "new" && (
                  <PhoneInput
                    value={phone}
                    onChange={handlePhoneChange}
                    onValidationChange={handlePhoneValidationChange}
                    placeholder="Enter phone number"
                    disabled={isLoading}
                  />
                )}

                {/* Send Code Button */}
                <button
                  onClick={handleSendCode}
                  disabled={isLoading || !phone || !phoneValidation.isValid}
                  className="group w-full h-14 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 text-white font-semibold hover:from-orange-600 hover:to-orange-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98] shadow-lg hover:shadow-xl"
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Sending...</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-2">
                      <span>Send Verification Code</span>
                      <FaArrowRight className="group-hover:translate-x-1 transition-transform" />
                    </div>
                  )}
                </button>
              </div>
            )}

            {codeSent && (
              <div className="space-y-6">
                {/* OTP Input */}
                <OTPInput
                  length={6}
                  value={code}
                  onChange={handleCodeChange}
                  onComplete={handleCodeComplete}
                  disabled={isLoading}
                  showTimer={false} // Timer is now shown in the banner
                  expiryTime={codeExpiry}
                  onResend={handleResendCode}
                  resendAttempts={resendAttempts}
                  maxResendAttempts={3}
                />

                {/* Support Contact */}
                <div className="text-center">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    Still having trouble?
                  </p>
                  <a 
                    href="mailto:support@iforge.io" 
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-gray-300 dark:border-navy-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-navy-800 transition-colors duration-200"
                  >
                    Contact Support
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      }
    />
  );
}

export default PhoneVerificationDefault;